import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import { authSessionUserSchema, type AuthSessionUser } from '@aia/platform-core';

import { api, authApi, isApiError, refreshAccessToken } from './api';

export type User = AuthSessionUser;
export type ProgressPulseRole = AuthSessionUser['role'];

/** Result of a password login. `mfaRequired` means the password was correct but
 * a second factor is needed; complete it with `verifyMfa(mfaToken, …)`. */
export interface LoginResult {
  success: boolean;
  error?: string;
  mfaRequired?: boolean;
  mfaToken?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True when a staff member signed in but their org requires MFA they haven't
   * enrolled yet — the app gates them to the Security page until they do. */
  mfaEnrollmentRequired: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<LoginResult>;
  verifyMfa: (
    mfaToken: string,
    opts: { code?: string; backupCode?: string },
  ) => Promise<{ success: boolean; error?: string }>;
  markMfaEnrolled: () => void;
  setAuth: (token: string, user: User) => void;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  enrollAndRegister: (data: EnrollAndRegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  forgotPassword: (
    email: string,
    turnstileToken?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (
    token: string,
    password: string,
    turnstileToken?: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  organizationId?: string;
  role?: string;
  isPlatformAdmin?: boolean;
  setupKey?: string;
}

interface EnrollAndRegisterData {
  code: string;
  firstName: string;
  lastName: string;
  email: string;
  // C7: omitted for magic-link tenants (learner signs in with an emailed OTP).
  password?: string;
  turnstileToken: string;
  // C14: COPPA/age attestation, recorded server-side at registration.
  coppaConsent: boolean;
}

export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return true;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return (payload.exp ?? 0) * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * True when the token is expired or within `skewSeconds` of expiry. Used to
 * renew proactively so an in-flight request never hits a 401 mid-task.
 */
export function isTokenExpiringSoon(token: string, skewSeconds = 120): boolean {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return true;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return (payload.exp ?? 0) * 1000 < Date.now() + skewSeconds * 1000;
  } catch {
    return true;
  }
}

const TOKEN_KEY = 'progresspulse_token';
const USER_KEY = 'progresspulse_user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      sessionStorage.setItem(TOKEN_KEY, storedToken);
      sessionStorage.setItem(USER_KEY, storedUser);
      setToken(storedToken);
      try {
        const parsed: unknown = JSON.parse(storedUser) as unknown;
        const result = authSessionUserSchema.safeParse(parsed);
        if (!result.success) {
          clearAuth();
          setIsLoading(false);
          return;
        }
        setUser(result.data as User);
      } catch {
        clearAuth();
        setIsLoading(false);
        return;
      }
      void verifyToken();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      const response = await authApi.get<User>('/auth/me');
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        clearAuth();
      }
    } catch {
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    setMfaEnrollmentRequired(false);
  }, []);

  // Periodic JWT expiry check. Rather than logging the user out when the
  // short-lived access token expires, renew it silently with the refresh-token
  // cookie. Only clear auth if the refresh itself fails (refresh token expired
  // or revoked). This keeps teachers logged in mid-setup and students mid-probe.
  useEffect(() => {
    let cancelled = false;

    const checkExpiry = async () => {
      const t = sessionStorage.getItem(TOKEN_KEY);
      if (!t || !isTokenExpiringSoon(t)) return;
      const refreshed = await refreshAccessToken();
      if (cancelled) return;
      if (refreshed) {
        const next = sessionStorage.getItem(TOKEN_KEY);
        if (next) setToken(next);
      } else {
        clearAuth();
      }
    };

    const interval = setInterval(() => void checkExpiry(), 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void checkExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clearAuth]);

  const persistAuth = useCallback((newToken: string, newUser: User) => {
    sessionStorage.setItem(TOKEN_KEY, newToken);
    sessionStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string): Promise<LoginResult> => {
      try {
        const response = await authApi.post<{
          user: User;
          token: string;
          mfaEnrollmentRequired?: boolean;
        }>('/auth/login', {
          email,
          password,
          turnstileToken,
        });

        if (response.success && response.data.user && response.data.token) {
          persistAuth(response.data.token, response.data.user);
          setMfaEnrollmentRequired(!!response.data.mfaEnrollmentRequired);
          return { success: true };
        }
        return { success: false, error: 'Login failed' };
      } catch (error) {
        // A7: a correct password on an MFA-enabled account returns 401 with an
        // MFA challenge — surface it so the UI can prompt for the second factor.
        if (isApiError(error) && error.status === 401) {
          const body = error.response as
            | { error?: { code?: string }; mfaToken?: string }
            | undefined;
          if (body?.error?.code === 'MFA_REQUIRED' && body.mfaToken) {
            return { success: false, mfaRequired: true, mfaToken: body.mfaToken };
          }
        }
        const errorMessage =
          error instanceof Error ? error.message : 'Login failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [persistAuth],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, opts: { code?: string; backupCode?: string }) => {
      try {
        const response = await authApi.post<{ user: User; token: string }>('/auth/mfa/verify', {
          mfaToken,
          ...opts,
        });
        if (response.success && response.data.user && response.data.token) {
          persistAuth(response.data.token, response.data.user);
          setMfaEnrollmentRequired(false);
          return { success: true };
        }
        return { success: false, error: 'Verification failed' };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Verification failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [persistAuth],
  );

  const markMfaEnrolled = useCallback(() => setMfaEnrollmentRequired(false), []);

  const register = useCallback(
    async (data: RegisterData) => {
      try {
        const response = await authApi.post<{ user: User; token: string }>('/auth/register', data);

        if (response.success && response.data.user && response.data.token) {
          persistAuth(response.data.token, response.data.user);
          return { success: true };
        }
        return { success: false, error: 'Registration failed' };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Registration failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [persistAuth],
  );

  const enrollAndRegister = useCallback(
    async (data: EnrollAndRegisterData) => {
      try {
        const response = await authApi.post<{ user: User; token?: string }>(
          '/progress/enrollment/register',
          data,
        );

        if (response.success && response.data?.user) {
          // Enrollment API historically returned firstName/lastName without `name`.
          // Normalize so LearnerDashboard ("Welcome, {firstName}!") and the sidebar work.
          const raw = response.data.user as User & {
            firstName?: string;
            lastName?: string;
          };
          const name =
            raw.name?.trim() ||
            [raw.firstName, raw.lastName].filter(Boolean).join(' ').trim() ||
            raw.email;
          const user: User = { ...raw, name };
          if (response.data.token) {
            persistAuth(response.data.token, user);
          } else {
            setUser(user);
            sessionStorage.setItem(USER_KEY, JSON.stringify(user));
          }
          return { success: true };
        }
        return { success: false, error: 'Enrollment registration failed' };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Enrollment failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [persistAuth],
  );

  const logout = useCallback(() => {
    void api.post<void>('/auth/logout').catch(() => {
      // logout error is non-critical
    });
    clearAuth();
  }, [clearAuth]);

  const forgotPassword = useCallback(async (email: string, turnstileToken?: string) => {
    try {
      // A6: identify the module so the reset link lands on ProgressPulse.
      const response = await authApi.post<null>('/auth/forgot-password', {
        email,
        turnstileToken,
        module: 'progress',
      });
      if (response.success) {
        return { success: true };
      }
      return { success: false, error: 'Failed to process request' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process request';
      return { success: false, error: errorMessage };
    }
  }, []);

  const resetPassword = useCallback(
    async (resetToken: string, password: string, turnstileToken?: string) => {
      try {
        const response = await authApi.post<null>('/auth/reset-password', {
          token: resetToken,
          password,
          turnstileToken,
        });

        if (response.success) {
          return { success: true };
        }
        return { success: false, error: 'Password reset failed' };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Password reset failed. Please try again.';
        return { success: false, error: errorMessage };
      }
    },
    [],
  );

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    mfaEnrollmentRequired,
    login,
    verifyMfa,
    markMfaEnrolled,
    setAuth: persistAuth,
    register,
    enrollAndRegister,
    logout,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const ROLE_HIERARCHY: Record<ProgressPulseRole, number> = {
  PlatformAdmin: 5,
  OrgAdmin: 4,
  UnitAdmin: 3,
  DataSteward: 3,
  Instructor: 2,
  Learner: 1,
};

export function hasMinimumRole(
  userRole: ProgressPulseRole,
  requiredRole: ProgressPulseRole,
): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}
